import { APPLICATION_PERIOD_TERM_INDEX, parseApplicationPeriodFromTerm } from './applicationPeriod';
import { parseChallengeCardBackground, parseChallengeCardSubtitle, parseChallengeCardTitle } from './challengeCard';
import { createDefaultState, createRowId, withDefaultSuccessSteps } from './defaults';
import { asNumber, asString, asStringArray, nonEmptyOrDefault, objectSection } from './parseHelpers';
import { defaultLogoImageId, resolveProgramDisplayName } from './programs';
import { filterToSiteRegionCodes } from './regions';
import { normalizeCampaignSlug } from './slug';
import type {
  CampaignMode,
  ConditionRow,
  FaqRow,
  PayloadBuilderState,
  ProgramConfig,
  SuccessStepRow,
  TextStepRow,
  TierMappingRow,
} from './types';

function hasPartnerBlock(payload: Record<string, unknown>): boolean {
  return Boolean(payload.partner && typeof payload.partner === 'object' && !Array.isArray(payload.partner));
}

function detectMode(payload: Record<string, unknown>): CampaignMode {
  return hasPartnerBlock(payload) ? 'statusClaim' : 'statusMatch';
}

function parseTierMappings(tiers: unknown): Array<TierMappingRow> {
  if (!Array.isArray(tiers)) return [];

  return tiers
    .map((tier) => {
      if (!tier || typeof tier !== 'object') return null;
      const row = tier as Record<string, unknown>;
      const sourceTier = asString(row.sourceTier);
      const targetSocieteTier = asString(row.targetSocieteTier);
      if (!sourceTier && !targetSocieteTier) return null;
      return {
        id: createRowId(),
        sourceTier,
        targetSocieteTier,
      };
    })
    .filter((row): row is TierMappingRow => row !== null);
}

function parsePrograms(value: unknown): Array<ProgramConfig> {
  if (!Array.isArray(value)) return [];
  return value
    .map((program) => {
      if (!program || typeof program !== 'object') return null;
      const row = program as Record<string, unknown>;
      const rawName = asString(row.name);
      if (!rawName) return null;
      const programName = resolveProgramDisplayName(rawName);
      return {
        id: createRowId(),
        programName,
        logoImageId: asString(row.logoImageId, defaultLogoImageId(programName)),
        tierMappings: parseTierMappings(row.tiers),
      };
    })
    .filter((program): program is ProgramConfig => program !== null);
}

function parseTextSteps(value: unknown): Array<TextStepRow> {
  if (!Array.isArray(value)) return [];
  return value
    .map((step) => {
      if (!step || typeof step !== 'object') return null;
      const row = step as Record<string, unknown>;
      return {
        id: createRowId(),
        title: asString(row.title),
        description: asString(row.description),
      };
    })
    .filter((step): step is TextStepRow => step !== null);
}

function mergeSuccessSteps(parsed: Array<SuccessStepRow>): Array<SuccessStepRow> {
  const defaults = withDefaultSuccessSteps(createDefaultState('statusMatch')).successSteps;
  return defaults.map((defaultStep) => {
    const existing = parsed.find((step) => step.key === defaultStep.key);
    if (!existing) return defaultStep;
    return {
      ...defaultStep,
      title: existing.title || defaultStep.title,
      description: existing.description || defaultStep.description,
    };
  });
}

function parseConditions(value: unknown): Array<ConditionRow> {
  if (!Array.isArray(value)) return [];
  return value
    .map((condition) => {
      if (!condition || typeof condition !== 'object') return null;
      const row = condition as Record<string, unknown>;
      return {
        id: createRowId(),
        icon: asString(row.icon, 'clock'),
        title: asString(row.title),
        description: asString(row.description),
      };
    })
    .filter((condition): condition is ConditionRow => condition !== null);
}

function parseFaqs(value: unknown): Array<FaqRow> {
  if (!Array.isArray(value)) return [];
  return value
    .map((faq) => {
      if (!faq || typeof faq !== 'object') return null;
      const row = faq as Record<string, unknown>;
      return {
        id: createRowId(),
        question: asString(row.question),
        answer: asString(row.answer),
      };
    })
    .filter((faq): faq is FaqRow => faq !== null);
}

export function parsePayloadJson(raw: string): PayloadBuilderState {
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch (error) {
    const message = error instanceof Error ? error.message : 'Invalid JSON';
    throw new Error(message);
  }

  if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
    throw new Error('Payload must be a JSON object');
  }

  const payload = parsed as Record<string, unknown>;
  const mode = detectMode(payload);
  const defaults = createDefaultState(mode);

  const hero = objectSection(payload.hero);
  const partner = objectSection(payload.partner);
  const secondary = objectSection(payload.secondarySection);
  const evidence = objectSection(payload.evidence);
  const challengeCard = objectSection(payload.challengeCard);

  const cardTitle = asString(challengeCard.cardTitle);
  const subtitleParts = parseChallengeCardSubtitle(asString(challengeCard.cardSubtitle));

  if (mode === 'statusClaim') {
    return {
      ...defaults,
      mode,
      campaignId: asString(payload.campaignId),
      slug: normalizeCampaignSlug(asString(payload.slug)),
      termsSlug: asString(payload.termsSlug, defaults.termsSlug),
      heroTitle: asString(hero.title, defaults.heroTitle),
      heroSubtitle: asString(hero.subtitle),
      heroImageId: asString(hero.imageId, defaults.heroImageId),
      partnerName: asString(partner.name),
      partnerLogoImageId: asString(partner.logoImageId),
      tiersGranted: asStringArray(payload.tiers),
      secondaryTitle: asString(secondary.title, defaults.secondaryTitle),
      secondarySubtitle: asString(secondary.subtitle, defaults.secondarySubtitle),
    };
  }

  const parsedSuccessSteps = Array.isArray(payload.successSteps)
    ? mergeSuccessSteps(
        payload.successSteps
          .map((step) => {
            if (!step || typeof step !== 'object') return null;
            const row = step as Record<string, unknown>;
            return {
              id: createRowId(),
              key: asString(row.key),
              title: asString(row.title),
              description: asString(row.description),
            };
          })
          .filter((step): step is SuccessStepRow => step !== null && Boolean(step.key)),
      )
    : defaults.successSteps;

  const terms = nonEmptyOrDefault(asStringArray(payload.terms), [...defaults.terms]);
  const applicationPeriod = parseApplicationPeriodFromTerm(terms[APPLICATION_PERIOD_TERM_INDEX] ?? '') ?? {
    from: defaults.applicationPeriodFrom,
    to: defaults.applicationPeriodTo,
  };

  return withDefaultSuccessSteps({
    ...defaults,
    mode,
    campaignId: asString(payload.campaignId),
    slug: normalizeCampaignSlug(asString(payload.slug)),
    termsSlug: asString(payload.termsSlug, defaults.termsSlug),
    heroTitle: asString(hero.title, defaults.heroTitle),
    heroSubtitle: asString(hero.subtitle, defaults.heroSubtitle),
    heroDescription: asString(hero.description, defaults.heroDescription),
    heroCtaText: asString(hero.ctaText, defaults.heroCtaText),
    heroImageId: asString(hero.imageId, defaults.heroImageId),
    allowedRegions:
      'allowedRegions' in payload ? filterToSiteRegionCodes(asStringArray(payload.allowedRegions)) : undefined,
    programs: parsePrograms(payload.programs),
    conditions: parseConditions(payload.conditions),
    howItWorks: nonEmptyOrDefault(parseTextSteps(payload.howItWorks), defaults.howItWorks),
    terms,
    applicationPeriodFrom: applicationPeriod.from,
    applicationPeriodTo: applicationPeriod.to,
    faqs: nonEmptyOrDefault(parseFaqs(payload.faqs), defaults.faqs),
    challengeCardTitleSuffix: cardTitle ? parseChallengeCardTitle(cardTitle) : defaults.challengeCardTitleSuffix,
    challengeCardSubtitleBefore: subtitleParts.before || defaults.challengeCardSubtitleBefore,
    challengeCardSubtitleAfter: subtitleParts.after || defaults.challengeCardSubtitleAfter,
    challengeCardBackground: parseChallengeCardBackground(challengeCard.background),
    evidenceMinPhotos: asNumber(evidence.minPhotos, defaults.evidenceMinPhotos),
    evidenceVerificationImageId: asString(evidence.verificationImageId, defaults.evidenceVerificationImageId),
    evidencePhotoHints: nonEmptyOrDefault(asStringArray(evidence.photoHints), defaults.evidencePhotoHints),
    evidenceAcceptedFormats: nonEmptyOrDefault(
      asStringArray(evidence.acceptedFormats),
      defaults.evidenceAcceptedFormats,
    ),
    evidenceProofRequirements: nonEmptyOrDefault(
      asStringArray(evidence.proofRequirements),
      defaults.evidenceProofRequirements,
    ),
    successSteps: parsedSuccessSteps,
  });
}
